import {
    Component,
    h,
    Watch,
    Prop,
    Element,
    Listen,
    State,
    Fragment,
    Method,
    EventEmitter,
    Event
} from '@stencil/core';

export interface OverlayOptions {
    modal: boolean;
}

@Component({
    tag: 'web-overlay',
    shadow: false
})
export class WebOverlay {
    @Element() rootElement: HTMLWebOverlayElement;

    /**
     * The visibility of the overlay.
     */
    @Prop() overlayVisible = false;

    /**
     * Option to present show full screen on mobile
     */
    @Prop() fullScreenOnMobile = true;

    /**
     * The anchored state of the overlay.
     */
    @Prop() anchored = false;

    /**
     * close button label
     */
    @Prop() closeButtonText = 'Close';

    /**
     * Modal (must interact) vs Modeless (may dismiss)
     */
    @Prop() modal = false;

    /**
     * Whether the overlay has a header.
     */
    @State() hasHeader: boolean;

    /**
     * Whether the overlay is modal.
     */
    @State() isModal: boolean;

    /**
     * Emitted when the overlay is toggled.
     */
    @Event() overlayToggle: EventEmitter<{ overlay: HTMLWebOverlayElement; toggle: boolean }>;

    /**
     * Watch handler for overlayVisible.
     * @param isVisible The visibility of the overlay.
     */
    @Watch('overlayVisible')
    watchOverlayVisibleHandler(isVisible: boolean) {
        this.setOverlayVisibility(isVisible);
    }

    /**
     * Watch handler for anchored.
     * @param isAnchored The anchored state of the overlay.
     */
    @Watch('anchored')
    watchAnchoredHandler(isAnchored: boolean) {
        this.rootElement.setAttribute('anchored', isAnchored === true ? 'true' : 'false');
    }

    /**
     * Opens the overlay.
     * @param options The options for opening the overlay.
     */
    @Method()
    async open(options?: OverlayOptions) {
        this.rootElement.setAttribute('overlay-visible', 'true');
        this.isModal = options?.modal ?? this.modal;
        this.overlayToggle.emit({ overlay: this.rootElement, toggle: true });
    }

    /**
     * Closes the overlay.
     */
    @Method()
    async close() {
        this.rootElement.setAttribute('overlay-visible', 'false');
        this.overlayToggle.emit({ overlay: this.rootElement, toggle: false });
    }

    /**
     * Handles keyup events. When the escape key is pressed, the overlay is dismissed.
     * @param e The event that was triggered.
     */
    @Listen('keyup', { target: 'document' })
    onKeyUp(e: KeyboardEvent) {
        if (this.overlayVisible && e.key === 'Escape') {
            this.dismiss();
        }
    }

    /**
     * Dismisses the overlay if it is not modal.
     */
    dismiss() {
        !this.isModal && this.close();
    }

    connectedCallback() {
        this.overlayVisible && this.setOverlayVisibility(this.overlayVisible);
        this.hasHeader = !!this.rootElement.querySelector('[slot="header"]');
        this.isModal = this.modal;
    }

    /**
     * Sets the visibility of the overlay.
     * @param isVisible The visibility of the overlay.
     */
    private setOverlayVisibility(isVisible: boolean) {
        const classes = this.rootElement.classList;
        classes.toggle('web-overlay--opened', isVisible);
        classes.toggle('web-overlay--closed', !isVisible);
        document.body.style.setProperty('overflow', isVisible ? 'hidden' : 'auto');
    }

    public render = () => (
        <Fragment>
            <div class="web-overlay-backdrop" onClick={() => this.dismiss()}></div>
            <dialog
                open={this.overlayVisible}
                class={{
                    'web-overlay-container': true,
                    'full-screen': this.fullScreenOnMobile
                }}
            >
                <div
                    class={{
                        'web-overlay-header': true,
                        'show-header': this.hasHeader || !this.isModal
                    }}
                >
                    <slot name="header"></slot>

                    {!this.isModal && (
                        <web-button
                            class="web-overlay-close"
                            type="plain"
                            icon-end="close-thin"
                            button-text={this.closeButtonText}
                            onClick={() => this.close()}
                        ></web-button>
                    )}
                </div>

                <div class="web-overlay-content">
                    <slot />
                </div>
            </dialog>
        </Fragment>
    );
}
